import { DecimalPipe } from '@angular/common'
import { computed, inject, Injectable } from '@angular/core'
import { CMLayerInput, CMSession, CsvRow } from '@core/models/calculation-module.model'
import { LayerInResults } from '@core/models/result-manager.model'
import { BusinessNameService } from '@core/services/business.service'
import { CalculationModuleService } from '@core/services/calculation-module.service'
import { CMSessionService } from '@core/services/cm-session.service'
import { LayerResultService } from '@core/services/layer-result.service'
import { environment } from '@env/environment'
import { LayerNamePipe } from '@shared/pipes/businessNameLayers.pipe'
import { AngularCsv as Angular2Csv } from 'angular-csv-ext/dist/Angular-csv'
import {
  BubbleDataPoint,
  ChartConfiguration,
  ChartData,
  ChartDataset,
  ChartType,
  ScatterDataPoint,
} from 'chart.js'

@Injectable({
  providedIn: 'root',
})
export class ExportDataService {
  private readonly _businessNameService = inject(BusinessNameService)
  private readonly _decimalPipe = inject(DecimalPipe)
  private readonly _layerResultService = inject(LayerResultService)
  private readonly _cmSessionService = inject(CMSessionService)
  private readonly _layerNamePipe = inject(LayerNamePipe)
  private readonly _calculationModuleService = inject(CalculationModuleService)

  activeCMSessions$ = this._cmSessionService.activeCMSessions$
  allLayerResults$ = this._layerResultService.allLayerResults$

  allCharts$ = computed(() => [
    ...this.activeCMSessions$().flatMap((session) => {
      const resultTitle = session.results.name + ' - ' + session.session_name
      return (session.results?.graphics || []).map((graphic) => ({
        ...graphic,
        resultTitle,
      }))
    }),
    ...this.allLayerResults$().layers.flatMap((layer) => {
      const resultTitle = this._layerNamePipe.transform(layer.name)
      return (layer?.graphics || []).map((graphic) => ({
        ...graphic,
        resultTitle,
      }))
    }),
  ])

  dataToCsv<T extends object | string>(
    data: T[],
    name: string,
    noDownload: boolean = false,
    headers: string[] = [],
  ) {
    const options = {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      useBom: true,
      noDownload: noDownload,
      headers: headers,
    }
    const file_name = environment.appName + '_' + name + '_' + this.generateDateForFileName()

    return new Angular2Csv(data, file_name, options).getCsvData()
  }

  generateDateForFileName(): string {
    const today = new Date()
    const hours = String(today.getHours()).padStart(2, '0')
    const minutes = String(today.getMinutes()).padStart(2, '0')
    return today.toLocaleDateString('en-CH') + '_' + hours + '.' + minutes
  }

  indicatorsToCSV(layerResult: LayerInResults[], cmSessions: CMSession[]): CsvRow[] {
    const array = []
    const header = {
      '1': 'name',
      '2': 'value',
      '3': 'unit',
    }

    // Go through all sessions
    if (cmSessions.length > 0) {
      // Add all the CM inputs
      for (const session of cmSessions) {
        array.push({ name: session.results.name + ' - ' + session.session_name })
        array.push({ '1': 'INPUT' })
        array.push(header)
        for (const [key, value] of Object.entries(session.payload.inputs)) {
          // Find the matching input_signature object by input_parameter_name
          let inputName = key
          let inputUnit = ''
          if (Array.isArray(session.payload.inputs_signature)) {
            const inputFromSignature = session.payload.inputs_signature.find(
              (sig) => sig.input_parameter_name === key,
            )
            if (inputFromSignature) {
              inputName = inputFromSignature.input_name
              // add unit if CM Input (and not Dynamic INput)
              if ('input_unit' in inputFromSignature) {
                inputUnit = inputFromSignature.input_unit as string
              }
            }
          }

          array.push({
            name: inputName,
            value: value,
            unit: inputUnit,
          })
        }

        // Add all the CM layer inputs
        //@todo, fix inconsistencies between standard layer and personal layers
        for (const layerInput of session.payload.layers_needed) {
          const correspondingName = session.payload.inputs_signature.filter((d) => {
            return (
              d &&
              this._calculationModuleService.isCMLayerInput(d) &&
              d.input_parameter_name === layerInput.input_parameter_name
            )
          }) as CMLayerInput[]

          let name = ''
          if (correspondingName.length == 1) {
            name = correspondingName[0].input_name
          }

          array.push({
            name: name,
            value: layerInput.name,
            unit: '',
          })
        }

        if (session.results.indicators.length > 0) {
          // Add all the CM outputs
          array.push({ '1': 'OUTPUT' })
          array.push(header)
          for (const indicator of session.results.indicators) {
            let valuePiped = indicator.value
            if (this._cmSessionService.isNumber(indicator.value)) {
              const transformed = this._decimalPipe.transform(indicator.value, '1.0-2')
              // No 1000-separator were asked for export
              valuePiped = transformed ? transformed.split(',').join('') : ''
            }

            array.push({
              name: indicator.name,
              value: valuePiped,
              unit: indicator.unit,
            })
          }
          array.push('')
        }
      }
      array.push('')
    }

    // Add all the layer outputs
    for (const layer of layerResult) {
      const displayLayerName = this._businessNameService.getReadableLayerName(layer.name)
      array.push({ name: displayLayerName })
      const indicators = layer.values
      array.push(header)

      for (const indicator of indicators) {
        const displayName = this._businessNameService.getReadableIndicatorsName(indicator.name)
        let valuePiped = this._decimalPipe.transform(indicator.value as string | number, '1.0-2')
        // No 1000-separator were asked for export
        valuePiped =
          valuePiped != null ? valuePiped.split(',').join('') : String(indicator.value ?? '')
        array.push({
          name: displayName,
          value: valuePiped,
          unit: indicator.unit,
        })
      }

      array.push('')
    }
    return array
  }
  chartsToCSV(
    graphs: (ChartConfiguration & { title?: string } & { resultTitle?: string })[],
  ): CsvRow[] {
    const arrayTmp: CsvRow[] = []

    // @TODO: remove any if solution found for graph.options.scales (not on all type of chart)
    graphs.forEach((graph: any) => {
      arrayTmp.push({ '1': graph.resultTitle ?? '' })
      arrayTmp.push({ 1: 'Chart title', '2': graph.title ?? '-' })

      const type = graph.type as ChartType
      const data: ChartData = graph.data
      const labels: string[] = Array.isArray(data.labels) ? data.labels.map(String) : []
      const datasets: ChartDataset[] = Array.isArray(data.datasets) ? data.datasets : []
      let label = ''

      if (type === 'pie' || type === 'doughnut') {
        datasets.forEach((dataset) => {
          label = dataset.label ?? ''
          arrayTmp.push({ '1': 'Unit', '2': label })
          arrayTmp.push({ '1': 'Label', '2': 'Value' })
          const values = Array.isArray(dataset.data) ? dataset.data : []
          labels.forEach((label, i) => {
            arrayTmp.push({ '1': label, '2': values[i]?.toString() ?? '' })
          })
        })
      } else if (type === 'bubble') {
        datasets.forEach((dataset) => {
          label = dataset.label ?? ''
          arrayTmp.push({ '1': 'Label', '2': label })
          arrayTmp.push({ '1': 'X', '2': 'Y', '3': 'Value' })
          const values = Array.isArray(dataset.data) ? (dataset.data as BubbleDataPoint[]) : []
          values.forEach((point) => {
            arrayTmp.push({
              '1': point?.x?.toString() ?? '',
              '2': point?.y?.toString() ?? '',
              '3': (point as BubbleDataPoint)?.r?.toString() ?? '',
            })
          })
        })
      } else if (type === 'scatter') {
        datasets.forEach((dataset) => {
          label = dataset.label ?? ''
          arrayTmp.push({ '1': 'Label', '2': label })
          if (Object.keys(graph.options.scales).length !== 0) {
            arrayTmp.push({ '1': 'X', '2': graph.options.scales.x.title.text })
            arrayTmp.push({ '1': 'Y', '2': graph.options.scales.y.title.text })
          }
          const values = Array.isArray(dataset.data) ? (dataset.data as ScatterDataPoint[]) : []
          values.forEach((point) => {
            arrayTmp.push({
              '1': point?.x?.toString() ?? '',
              '2': point?.y?.toString() ?? '',
            })
          })
        })
      } else {
        // Line, bar, etc.
        if (
          datasets.length === 1 ||
          datasets.some(
            (ds) => Array.isArray(ds.data) && (ds.data.length === 8760 || ds.data.length === 365),
          )
        ) {
          datasets.forEach((ds) => {
            arrayTmp.push({ '1': ds.label ?? 'Series' })
            const xTitle =
              typeof graph?.options?.scales?.x === 'object' &&
              'title' in (graph.options.scales.x as any) &&
              typeof (graph.options.scales.x as any).title?.text === 'string'
                ? (graph.options.scales.x as any).title.text
                : ''
            const yTitle =
              typeof graph?.options?.scales?.y === 'object' &&
              'title' in (graph.options.scales.y as any) &&
              typeof (graph.options.scales.y as any).title?.text === 'string'
                ? (graph.options.scales.y as any).title.text
                : ''
            arrayTmp.push({ a: xTitle, '2': yTitle })
            const dataArray = Array.isArray(ds.data) ? ds.data : []
            let labelArray: string[] = []
            if (dataArray.length === 8760) {
              labelArray = this._generateHourlyDates()
            } else if (dataArray.length === 365) {
              labelArray = this._generateDailyDates()
            } else {
              labelArray = labels
            }
            dataArray.forEach((val, idx) => {
              arrayTmp.push({ '1': labelArray[idx] ?? '', '2': val?.toString() ?? '' })
            })
          })
        } else {
          // Multi-dataset, multi-label
          // Axes
          if (Object.keys(graph.options.scales).length !== 0) {
            arrayTmp.push({ '1': 'X', '2': graph.options.scales.x.title.text })
            arrayTmp.push({ '1': 'Y', '2': graph.options.scales.y.title.text })
          }
          // Header row
          const header: { [key: string]: string } = { '1': '' }
          datasets.forEach((ds, idx) => {
            header[(idx + 2).toString()] = ds.label ?? ''
          })
          arrayTmp.push(header)

          // Data rows
          labels.forEach((label, i) => {
            const row: { [key: string]: string } = { '1': label }
            datasets.forEach((ds, idx) => {
              const dataArr = Array.isArray(ds.data) ? ds.data : []
              row[(idx + 2).toString()] = dataArr[i]?.toString() ?? ''
            })
            arrayTmp.push(row)
          })
        }
      }
      arrayTmp.push('')
    })
    return arrayTmp
  }

  exportIndicators(tabSelected: string) {
    const arrayTmp = this.indicatorsToCSV(this.allLayerResults$().layers, this.activeCMSessions$())
    this.dataToCsv(arrayTmp, tabSelected)
  }

  exportGraphic(tabSelected: string) {
    const arrayTmp = this.chartsToCSV(this.allCharts$())
    this.dataToCsv(arrayTmp, tabSelected)
  }

  private _months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
  // @TODO recheck this code
  private _generateHourlyDates() {
    const startDate = new Date('2000-01-01T00:00:00')
    const endDate = new Date('2000-12-31T23:00:00')
    const datesArray = []
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const day = String(currentDate.getDate()).padStart(2, '0')
      const month = this._months[currentDate.getMonth()]
      const hours = String(currentDate.getHours()).padStart(2, '0')

      const formattedDate = `${day}-${month}-2000 ${hours}:00`
      datesArray.push(formattedDate)

      currentDate.setHours(currentDate.getHours() + 1)
    }
    return datesArray
  }
  // @TODO recheck this code
  private _generateDailyDates() {
    const startDate = new Date('2000-01-01T00:00:00')
    const endDate = new Date('2000-12-31T00:00:00')
    const datesArray = []

    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const day = String(currentDate.getDate()).padStart(2, '0')
      const month = this._months[currentDate.getMonth()]
      const formattedDate = `${day}-${month}-2000 00:00 2000`
      datesArray.push(formattedDate)

      currentDate.setDate(currentDate.getDate() + 1)
    }

    return datesArray
  }
}
